import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';
import logger from './utils/logger';

function MonthlyFeeManagement() {
  const getTodayDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getCurrentMonth = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [companyOptions, setCompanyOptions] = useState([]);
  const [company, setCompany] = useState('');
  const [amount, setAmount] = useState('');
  const [receivedDate, setReceivedDate] = useState(getTodayDate());
  const [note, setNote] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [listMonth, setListMonth] = useState(getCurrentMonth());
  const [listPage, setListPage] = useState(1);
  const [listLimit] = useState(20);
  const [listTotalPages, setListTotalPages] = useState(0);
  const [listTotal, setListTotal] = useState(0);
  const [listError, setListError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const formatNumber = (value) => new Intl.NumberFormat('ko-KR').format(Math.floor(value || 0));
  const formatCurrency = (value) => `${formatNumber(value)}원`;

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/companies', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyOptions(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      logger.apiError('GET', '/api/companies', err);
      setError('분류 목록을 불러오지 못했습니다.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchList = async () => {
    try {
      setLoadingList(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/monthly-fees?month=${listMonth}&page=${listPage}&limit=${listLimit}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setListTotalPages(response.data?.pagination?.totalPages || 0);
      setListTotal(response.data?.pagination?.total || 0);
      setListError('');
    } catch (err) {
      logger.apiError('GET', '/api/monthly-fees', err);
      setListError('월 이용료 내역을 불러오지 못했습니다.');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchList();
  }, [listMonth, listPage]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!company) {
      setError('분류를 선택해주세요.');
      return;
    }
    const feeAmount = Number(amount);
    if (!Number.isFinite(feeAmount) || feeAmount < 0) {
      setError('금액은 0 이상의 숫자여야 합니다.');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post('/api/monthly-fees', {
        company,
        amount: feeAmount,
        received_date: receivedDate,
        note
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('월 이용료 내역이 저장되었습니다.');
      setCompany('');
      setAmount('');
      setReceivedDate(getTodayDate());
      setNote('');
      await fetchList();
    } catch (err) {
      logger.apiError('POST', '/api/monthly-fees', err);
      setError(err?.response?.data?.error || '월 이용료 저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingRow({
      id: item.id,
      company: item.company,
      amount: String(item.amount ?? ''),
      receivedDate: item.received_date,
      note: item.note || ''
    });
    setError('');
    setSuccess('');
  };

  const handleEditChange = (field, value) => {
    setEditingRow((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editingRow) return;
    setError('');
    setSuccess('');

    const feeAmount = Number(editingRow.amount);
    if (!Number.isFinite(feeAmount) || feeAmount < 0) {
      setError('금액은 0 이상의 숫자여야 합니다.');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.put(`/api/monthly-fees/${editingRow.id}`, {
        company: editingRow.company,
        amount: feeAmount,
        received_date: editingRow.receivedDate,
        note: editingRow.note
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('월 이용료 내역이 수정되었습니다.');
      setEditingRow(null);
      await fetchList();
    } catch (err) {
      logger.apiError('PUT', '/api/monthly-fees/:id', err);
      setError(err?.response?.data?.error || '월 이용료 수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
  };

  const handleDelete = async (item) => {
    const confirmDelete = window.confirm('월 이용료 내역을 삭제할까요?');
    if (!confirmDelete) return;

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.delete(`/api/monthly-fees/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (editingRow?.id === item.id) {
        setEditingRow(null);
      }
      setSuccess('월 이용료 내역이 삭제되었습니다.');
      await fetchList();
    } catch (err) {
      logger.apiError('DELETE', '/api/monthly-fees/:id', err);
      setError(err?.response?.data?.error || '월 이용료 삭제에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.received_date === b.received_date) {
        return b.id - a.id;
      }
      return a.received_date < b.received_date ? 1 : -1;
    });
  }, [items]);

  return (
    <div className="settlement-input-layout">
      <div className="settlement-input-card">
        <h1>월 이용료 정산</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="monthlyFeeDate">수납 날짜</label>
            <input
              id="monthlyFeeDate"
              type="date"
              value={receivedDate}
              onChange={(event) => setReceivedDate(event.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="monthlyFeeCompany">분류</label>
            <select
              id="monthlyFeeCompany"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              disabled={loadingCompanies}
            >
              <option value="">분류 선택</option>
              {companyOptions.map((item) => (
                <option key={item.id} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="monthlyFeeAmount">수납 금액</label>
            <input
              id="monthlyFeeAmount"
              type="number"
              min="0"
              step="1"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="수납 금액을 입력하세요"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="monthlyFeeNote">메모</label>
            <input
              id="monthlyFeeNote"
              type="text"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="메모 (선택)"
            />
          </div>
          <button className="submit-btn" type="submit" disabled={submitting}>
            {submitting ? '저장 중...' : '월 이용료 저장'}
          </button>
        </form>

        {error && (
          <div className="error">
            <h3>오류</h3>
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="result">
            <h3>완료</h3>
            <p>{success}</p>
          </div>
        )}
      </div>

      <div className="settlement-input-card">
        <div className="settlement-input-list-header">
          <div>
            <h2>월 이용료 내역</h2>
            <div className="settlement-input-list-meta">
              총 {formatNumber(listTotal)}건
            </div>
          </div>
          <div className="settlement-input-list-controls">
            <label htmlFor="monthlyFeeMonth">월 선택</label>
            <input
              id="monthlyFeeMonth"
              type="month"
              value={listMonth}
              onChange={(event) => {
                setListMonth(event.target.value);
                setListPage(1);
              }}
            />
          </div>
        </div>

        {loadingList ? (
          <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>불러오는 중...</div>
        ) : listError ? (
          <div className="error" style={{ marginTop: 0 }}>
            <h3>오류</h3>
            <p>{listError}</p>
          </div>
        ) : sortedItems.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>내역이 없습니다.</div>
        ) : (
          <table className="deposit-table">
            <thead>
              <tr>
                <th>수납일</th>
                <th>분류</th>
                <th>금액</th>
                <th>메모</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item) => {
                const isEditing = editingRow?.id === item.id;
                return (
                  <tr key={item.id}>
                    <td>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editingRow.receivedDate}
                          onChange={(event) => handleEditChange('receivedDate', event.target.value)}
                          className="settlement-inline-input"
                        />
                      ) : (
                        item.received_date
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <select
                          value={editingRow.company}
                          onChange={(event) => handleEditChange('company', event.target.value)}
                          className="settlement-inline-input"
                        >
                          {companyOptions.map((opt) => (
                            <option key={opt.id} value={opt.name}>
                              {opt.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        item.company
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={editingRow.amount}
                          onChange={(event) => handleEditChange('amount', event.target.value)}
                          className="settlement-inline-input"
                        />
                      ) : (
                        formatCurrency(item.amount)
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingRow.note}
                          onChange={(event) => handleEditChange('note', event.target.value)}
                          className="settlement-inline-input"
                        />
                      ) : (
                        item.note || '-'
                      )}
                    </td>
                    <td className="monthly-fee-actions">
                      {isEditing ? (
                        <div className="settlement-edit-actions">
                          <button
                            type="button"
                            onClick={handleSaveEdit}
                            className="settlement-save-button"
                            disabled={submitting}
                          >
                            저장
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="settlement-cancel-button"
                            disabled={submitting}
                          >
                            취소
                          </button>
                        </div>
                      ) : (
                        <div className="settlement-edit-actions">
                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            className="settlement-edit-button"
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item)}
                            className="settlement-delete-button"
                          >
                            삭제
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {listTotalPages > 1 && (
          <div className="pagination">
            <button
              type="button"
              onClick={() => setListPage((prev) => Math.max(1, prev - 1))}
              disabled={listPage <= 1}
            >
              이전
            </button>
            <button type="button" disabled>
              {listPage} / {listTotalPages}
            </button>
            <button
              type="button"
              onClick={() => setListPage((prev) => Math.min(listTotalPages, prev + 1))}
              disabled={listPage >= listTotalPages}
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MonthlyFeeManagement;
